import { motion } from "framer-motion";

const steps = [
  { label: "Observe", desc: "Sensors, vision, logs, ops chatter — captured and contextualized.", accent: "#00E5FF" },
  { label: "Model",   desc: "Domain-tuned models — perception, language, planning, optimization.", accent: "#DFFF00" },
  { label: "Automate",desc: "Decisions become actions — PLC, ticket, agent, or operator nudge.", accent: "#FF4500" },
  { label: "Measure", desc: "Live KPIs, drift, false-positives, throughput, downtime, energy.", accent: "#FF4500" },
  { label: "Improve", desc: "Loops retrain. Setpoints tune. The system keeps getting sharper.", accent: "#DFFF00" },
];

export default function SystemThinks() {
  const R = 175; // ring radius for desktop
  const center = 220;

  return (
    <section data-testid="system-thinks" className="relative px-6 md:px-14 py-24 md:py-32 border-b border-[#2A2A2A] overflow-hidden">
      <div className="absolute inset-0 ww-radial-vermilion opacity-40 pointer-events-none" />

      <div className="relative max-w-6xl grid md:grid-cols-12 gap-12 items-center">
        <div className="md:col-span-5">
          <div className="text-overline mb-6">◢ Methodology</div>
          <h2 className="font-display font-semibold tracking-tighter text-4xl sm:text-5xl text-white leading-[1.05]">
            How the system <span className="text-[#FF4500]">thinks</span>.
          </h2>
          <p className="mt-5 text-[#8E8E93] text-[15px] leading-relaxed max-w-md">
            We don't ship demos that die in pilot. We deploy intelligent loops — each iteration
            sharpening the next. The loop is the product.
          </p>

          <ul className="mt-8 space-y-3 font-mono text-[12px] tracking-[0.1em] text-[#F5F5F5]">
            {steps.map((s, i) => (
              <li key={s.label} className="flex items-center gap-3">
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: s.accent }} />
                <span className="text-[#8E8E93]">{String(i + 1).padStart(2, "0")}</span>
                <span className="uppercase">{s.label}</span>
                <span className="text-[#555] font-body normal-case text-[12px]"> · {s.desc}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="md:col-span-7 flex justify-center" data-testid="system-loop-diagram">
          <svg viewBox="0 0 440 440" className="w-full max-w-[440px]">
            <defs>
              <radialGradient id="centerGlow" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="rgba(255,69,0,0.4)" />
                <stop offset="100%" stopColor="rgba(255,69,0,0)" />
              </radialGradient>
            </defs>

            {/* outer ring */}
            <circle cx={center} cy={center} r={R + 20} fill="none" stroke="#1A1A1A" strokeWidth="1" />
            <circle cx={center} cy={center} r={R} fill="none" stroke="#2A2A2A" strokeWidth="1" />
            <circle cx={center} cy={center} r={R - 22} fill="none" stroke="#1A1A1A" strokeWidth="1" />

            {/* center */}
            <circle cx={center} cy={center} r={70} fill="url(#centerGlow)" />
            <circle cx={center} cy={center} r={4} fill="#FF4500" />

            {/* dashed flow ring */}
            <circle
              cx={center}
              cy={center}
              r={R}
              fill="none"
              stroke="#FF4500"
              strokeOpacity="0.65"
              strokeWidth="1.4"
              className="ww-dash"
            />

            {/* nodes */}
            {steps.map((s, i) => {
              const angle = (i / steps.length) * Math.PI * 2 - Math.PI / 2;
              const x = center + Math.cos(angle) * R;
              const y = center + Math.sin(angle) * R;
              return (
                <g key={s.label}>
                  <circle cx={x} cy={y} r={9} fill="#050505" stroke={s.accent} strokeWidth="1.5" />
                  <circle cx={x} cy={y} r={3} fill={s.accent}>
                    <animate attributeName="opacity" values="1;0.3;1" dur={`${2 + i * 0.3}s`} repeatCount="indefinite" />
                  </circle>
                  <text
                    x={x + (Math.cos(angle) > 0 ? 16 : -16)}
                    y={y + 4}
                    fontFamily="JetBrains Mono"
                    fontSize="11"
                    fill="#F5F5F5"
                    textAnchor={Math.cos(angle) > 0 ? "start" : "end"}
                    style={{ textTransform: "uppercase", letterSpacing: "0.18em" }}
                  >
                    {s.label}
                  </text>
                </g>
              );
            })}

            {/* center label */}
            <text x={center} y={center - 6} textAnchor="middle" fontFamily="JetBrains Mono" fontSize="10" fill="#8E8E93" letterSpacing="3">
              INTELLIGENT
            </text>
            <text x={center} y={center + 10} textAnchor="middle" fontFamily="JetBrains Mono" fontSize="10" fill="#8E8E93" letterSpacing="3">
              LOOP
            </text>
          </svg>
        </div>
      </div>
    </section>
  );
}
